import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  ColumnDef,
  OnChangeFn,
  RowSelectionState,
  SortingState,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { Inbox } from "lucide-react";
import { useEffect, useState } from "react";
import { BatchDecisionBar } from "./batch-decision-bar";
import { PAYROLL_VOUCHER_LABELS } from "../messages";
import { PayrollVoucher, VoucherDecision } from "../types";
import { VoucherTableMeta } from "../columns";
import { ariaSortOf } from "@/utils/aria-sort";
import { AssignmentsEnum } from "@/enum/assignments";
import { useAssignmentAbilities } from "@/hooks/use-assignment-permission";

interface DataTableProps {
  columns: ColumnDef<PayrollVoucher>[];
  data: PayrollVoucher[];
  sorting: SortingState;
  onSortingChange: OnChangeFn<SortingState>;
  onDecide: (
    voucher: PayrollVoucher,
    decision: VoucherDecision,
  ) => Promise<void>;
  onDecideBatch: (
    vouchers: readonly PayrollVoucher[],
    decision: VoucherDecision,
  ) => Promise<boolean>;
}

const ROW_CELL_CLASS_NAME: string = "!h-auto !py-4";

const HEAD_CELL_CLASS_NAME: string = "!h-auto !py-3";

export function DataTable({
  columns,
  data,
  sorting,
  onSortingChange,
  onDecide,
  onDecideBatch,
}: DataTableProps): JSX.Element {
  const { canUpdate } = useAssignmentAbilities(AssignmentsEnum.PAYROLL_VOUCHER);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  const table = useReactTable({
    data,
    columns,
    state: { sorting, rowSelection },
    meta: { canDecide: canUpdate, onDecide } satisfies VoucherTableMeta,
    getRowId: (voucher: PayrollVoucher): string => String(voucher.id),
    enableRowSelection: true,
    onRowSelectionChange: setRowSelection,
    onSortingChange,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    manualSorting: true,
  });

  const visibleIds: string = data
    .map((voucher: PayrollVoucher): number => voucher.id)
    .join(",");

  useEffect((): void => {
    setRowSelection({});
  }, [visibleIds]);

  const selected: PayrollVoucher[] = table
    .getSelectedRowModel()
    .rows.map((row) => row.original);

  const decideBatchAndClear = async (
    vouchers: readonly PayrollVoucher[],
    decision: VoucherDecision,
  ): Promise<void> => {
    const accepted: boolean = await onDecideBatch(vouchers, decision);

    if (!accepted) return;

    setRowSelection({});
  };

  return (
    <div className="hidden md:block">
      {canUpdate && selected.length > 0 && (
        <BatchDecisionBar
          selected={selected}
          onDecideBatch={decideBatchAndClear}
        />
      )}

      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
      <Table>
        <TableHeader className="bg-gray-50/80">
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHead
                  key={header.id}
                  colSpan={header.colSpan}
                  className={HEAD_CELL_CLASS_NAME}
                  aria-sort={ariaSortOf(
                    header.column.getCanSort(),
                    header.column.getIsSorted(),
                    header.column.getSortIndex(),
                  )}
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.length === 0 && (
            <TableRow>
              <TableCell
                colSpan={columns.length}
                className="!h-auto !py-16 text-center"
              >
                <div className="flex flex-col items-center gap-3 text-muted-foreground">
                  <Inbox className="h-8 w-8" />
                  {PAYROLL_VOUCHER_LABELS.EMPTY_STATE}
                </div>
              </TableCell>
            </TableRow>
          )}

          {table.getRowModel().rows.map((row) => (
            <TableRow
              key={row.id}
              className="align-top"
              data-state={row.getIsSelected() ? "selected" : undefined}
            >
              {row.getVisibleCells().map((cell) => (
                <TableCell key={cell.id} className={ROW_CELL_CLASS_NAME}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      </div>
    </div>
  );
}
